/**
 * `cuttingPlaneMath.ts` — pure Unity-space rotation math for the cutting
 * plane. Model-layer code (no Three.js import allowed here) needs to
 * round-trip the persisted pose contract from
 * `CommonSliceToolModel.cs`:
 *
 *   - Persisted rotation = euler degrees (Unity `Quaternion.Euler`, ZXY
 *     intrinsic — `q = Ry(y) * Rx(x) * Rz(z)`), scene-root-relative,
 *     WITHOUT the 180°-about-Y "gizmo" offset.
 *   - The live Unity plane GameObject carries the extra 180°-about-Y:
 *     `rootRot = persistedRot * Ry(180)` (`UpdateLocalPose`), and the
 *     inspector displays the ROOT euler.
 *   - Kept half-space: `dot(p - position, forwardOfEuler(persisted)) >= 0`
 *     (see `SlicePlane.LateUpdate` — the shader normal is the negated
 *     Circle forward, and the Circle sits under the 180°-Y FlipDirection
 *     child, so the discard side is `-persistedForward`).
 *
 * Quaternions here are Unity-space `[x, y, z, w]` tuples (same layout as
 * `ParticipantQuat`); all angles are degrees.
 */

import type { Vec3 } from "@models/data/entityData";
import {
  normalizeDegrees,
  quatMultiply,
  rotateVecByQuat,
  unityEulerToQuat,
  unityQuatToEuler as unityQuatToEulerRanged,
  type UnityQuat,
} from "@models/utils/unityBasis";

/**
 * The generic Unity rotation math moved to `@models/utils/unityBasis` in task
 * 0004 — it was one of three copies, and the copies had drifted (this one was
 * the only one with a gimbal-lock fold, and that fold had a sign error at pitch
 * +90°, i.e. a horizontal cutting plane). Re-exported here so the six existing
 * importers keep working; new code should import from `unityBasis` directly.
 */
export { quatMultiply, rotateVecByQuat, unityEulerToQuat };
export type { UnityQuat };

/**
 * Unity `Quaternion.eulerAngles` for the ZXY composition, in Unity's `[0, 360)`
 * range. Thin wrapper: the shared helper takes the range explicitly, because
 * the entity-snapshot path deliberately persists a signed range instead.
 */
export function unityQuatToEuler(q: UnityQuat): Vec3 {
  return unityQuatToEulerRanged(q, "unity");
}

/** `persistedRot * Ry(180)` factor (Unity `m_gizmoRotation`). */
export const GIZMO_ROTATION_Y180: UnityQuat = [0, 1, 0, 0];

/** Forward (+Z) axis of a persisted euler — the KEPT-side direction. */
export function forwardOfEuler(euler: Vec3): Vec3 {
  return rotateVecByQuat([0, 0, 1], unityEulerToQuat(euler));
}

/**
 * Unity `Quaternion.LookRotation(forward)` (world up) as euler degrees —
 * used by `FlipCommonSlice` semantics where Unity assigns
 * `transform.forward` and lets the engine re-derive a canonical rotation.
 * Degenerate straight-up/down forwards pitch ±90 with yaw preserved at 0.
 */
export function lookRotationEuler(forward: Vec3): Vec3 {
  const [fx, fy, fz] = forward;
  const len = Math.hypot(fx, fy, fz);
  if (len < 1e-8) return [0, 0, 0];
  const nx = fx / len;
  const ny = fy / len;
  const nz = fz / len;
  const pitch = -Math.asin(clamp(ny, -1, 1)) * K_RAD_TO_DEG;
  const yaw = Math.abs(ny) > 0.99999 ? 0 : Math.atan2(nx, nz) * K_RAD_TO_DEG;
  return [normalizeDeg(pitch), normalizeDeg(yaw), 0];
}

/** Persisted euler → the euler Unity's inspector shows (root = persisted ∘ Ry180). */
export function persistedEulerToRootEuler(euler: Vec3): Vec3 {
  return unityQuatToEuler(quatMultiply(unityEulerToQuat(euler), GIZMO_ROTATION_Y180));
}

/** Inverse of {@link persistedEulerToRootEuler} (Ry(-180) ≡ Ry(180)). */
export function rootEulerToPersistedEuler(euler: Vec3): Vec3 {
  return unityQuatToEuler(quatMultiply(unityEulerToQuat(euler), GIZMO_ROTATION_Y180));
}

const K_RAD_TO_DEG = 180 / Math.PI;

function normalizeDeg(value: number): number {
  return normalizeDegrees(value, "unity");
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
